<template>
  <v-btn
    :variant="isInstructionTypeSelected ? 'outlined' : 'flat'"
    :active="isInstructionTypeSelected"
    :color="isInstructionTypeSelected ? 'success' : ''"
    class="text-h6 px-8"
    width="200"
    :loading="updateInProgress"
  >
    Patrols<v-icon size="18" icon="mdi-chevron-down" />
    <v-menu activator="parent" max-width="400" :close-on-content-click="false" v-model="isMenuOpen">
      <ErrorAlert
        v-if="updateError != null"
        :error="updateError"
        @clearErrors="clearUpdateError()"
        class="mt-0 mb-4 rounded"
      />
      <v-list density="compact" nav class="px-0">
        <div ref="patrolList" class="patrol-list">
          <v-list-item
            nav
            v-for="(instruction, i) in localInstructions"
            :key="i"
            @click.stop="selectInstruction(instruction)"
            class="patrol-instruction-item py-2"
          >
            <template
              v-slot:prepend
              v-if="
                localInstructions.length > 1 &&
                currentUser?.hasPermission('post_orders.change_postorderinstructions')
              "
            >
              <v-icon class="handle" style="cursor: move" size="20" icon="mdi-drag-vertical" />
            </template>

            <template v-slot:title>
              <span :class="{ 'ml-8': localInstructions.length === 1 }">{{
                instruction.form.title
              }}</span>
            </template>
          </v-list-item>
        </div>

        <v-divider
          v-if="
            localInstructions.length > 0 &&
            currentUser?.hasPermission('post_orders.add_postorderinstructions')
          "
        />
        <v-list-item
          v-if="currentUser?.hasPermission('post_orders.add_postorderinstructions')"
          @click="createInstruction(PostOrderInstructionTypeEnum.Patrol)"
        >
          <v-list-item-title>
            <v-icon class="mr-2" size="20" icon="mdi-plus-circle" color="medium-emphasis" /> Add new
            Patrol</v-list-item-title
          >
        </v-list-item>
      </v-list>
    </v-menu>
  </v-btn>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useDraggable } from 'vue-draggable-plus'

import { useAuthStore } from '@/stores'
import {
  type IPostOrderInstruction,
  PostOrderInstruction,
  PostOrderInstructionTypeEnum
} from '@/models/post-order'
import { useUpdatePostOrderInstructionsOrders } from '@/composables/post-order'
import ErrorAlert from '../common/ErrorAlert.vue'

interface Props {
  instructions?: IPostOrderInstruction[]
  isInstructionTypeSelected: boolean
  postOrderId: number
}

const VUE_MENU_WAIT_FOR_DOM_MS = 50

const props = withDefaults(defineProps<Props>(), {
  instructions: (): IPostOrderInstruction[] => []
})

const authStore = useAuthStore()
const { getUser: currentUser } = storeToRefs(authStore)

interface Emits {
  (e: 'select-instruction', instruction: IPostOrderInstruction): void
  (e: 'create-instruction', type: PostOrderInstructionTypeEnum): void
  (e: 'no-instruction-selected', type: PostOrderInstructionTypeEnum): void
}
const emit = defineEmits<Emits>()

// Local state for draggable instructions
const localInstructions = ref<IPostOrderInstruction[]>([])
const patrolList = ref<HTMLDivElement | null>(null)
const isMenuOpen = ref(false)

// Watch for changes in props.instructions and update local state
watch(
  () => props.instructions,
  (value) => {
    if (value) {
      localInstructions.value = [...value]
    }
  },
  { immediate: true, deep: true }
)

// Initialize draggable functionality
let draggableInstance: ReturnType<typeof useDraggable> | null = null

function destroyDraggable() {
  if (draggableInstance) {
    draggableInstance.destroy()
    draggableInstance = null
  }
}

function initializeDraggable() {
  // Destroy existing instance first
  destroyDraggable()

  // Only initialize if we have permissions, instructions, and a DOM element
  const canDrag = currentUser.value?.hasPermission('post_orders.change_postorderinstructions')

  if (!patrolList.value || localInstructions.value.length <= 1 || !canDrag || !isMenuOpen.value) {
    return // Don't initialize if conditions aren't met
  }

  draggableInstance = useDraggable(patrolList, localInstructions, {
    onEnd: handleInstructionsReordered,
    handle: '.handle',
    animation: 150,
    ghostClass: 'sortable-ghost',
    dragClass: 'sortable-drag'
  })
  draggableInstance.start()
}

// Watch for menu opening - this is when DOM elements actually exist
watch(isMenuOpen, (isOpen) => {
  if (isOpen) {
    // Menu just opened, DOM elements should now exist
    // Wait a bit for Vue to finish rendering
    setTimeout(() => {
      initializeDraggable()
    }, VUE_MENU_WAIT_FOR_DOM_MS)
  } else {
    // Menu closed, clean up draggable
    destroyDraggable()
  }
})

onUnmounted(() => {
  destroyDraggable()
})

function createInstruction(type: PostOrderInstructionTypeEnum) {
  emit('create-instruction', type)
}

function selectInstruction(instruction: IPostOrderInstruction | null) {
  if (!instruction) {
    emit('no-instruction-selected', PostOrderInstructionTypeEnum.Patrol)
  } else {
    emit('select-instruction', instruction)
  }
}

function compareInstructionOrder(otherArray: IPostOrderInstruction[]) {
  return function (current: IPostOrderInstruction) {
    return (
      otherArray.filter(function (other: IPostOrderInstruction) {
        return other.id == current.id && other.order == current.order
      }).length == 0
    )
  }
}

const {
  mutate: updateInstructionOrders,
  isPending: updateInProgress,
  error: updateError,
  reset: clearUpdateError
} = useUpdatePostOrderInstructionsOrders(computed(() => props.postOrderId))

function handleInstructionsReordered() {
  clearUpdateError()
  const instructionsClone: IPostOrderInstruction[] = [...localInstructions.value]

  const instructions = instructionsClone.map(
    (instruction: IPostOrderInstruction, index: number) => {
      return new PostOrderInstruction({
        ...instruction,
        order: index + 1
      })
    }
  )

  const difference = instructions.filter(compareInstructionOrder(instructionsClone))

  const mappedDifference = difference.map((instruction) => {
    return {
      id: instruction.id,
      order: instruction.order
    }
  })

  updateInstructionOrders(mappedDifference, {
    onError() {
      localInstructions.value = [...props.instructions]
    }
  })
}
</script>

<style scoped>
.patrol-list :deep(.v-list-item .v-list-item__prepend) {
  display: block !important;
}
</style>
